import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CornerDownLeft, FileText, MapPin, Megaphone, BookOpen, Sparkles, Trophy } from 'lucide-react'
import { suggestSearch, type Suggestion, type SearchType } from '../../services/searchService'

const TYPE_ICON: Record<SearchType, typeof FileText> = {
  ARTICLE: FileText,
  RESOURCE: BookOpen,
  FACILITY: MapPin,
  CAMPAIGN: Megaphone,
  COMPETITION: Trophy,
  TESTIMONIAL: Sparkles,
}

interface Props {
  /** Controlled input value. */
  value: string
  onChange: (v: string) => void
  /** Called when the user picks a suggestion (click or Enter on highlighted). */
  onSelectSuggestion: (s: Suggestion) => void
  /** Called when the user submits raw typed text with no highlighted suggestion. */
  onSubmit: (q: string) => void
  placeholder?: string
  inputId?: string
  srLabel?: string
  /** Extra classes for the input (e.g. padding for a leading icon). */
  inputClassName?: string
  /** Optional leading-icon node rendered inside the input wrapper. */
  leadingIcon?: React.ReactNode
  autoFocus?: boolean
  /** When true the dropdown is anchored below the input inside this component. */
  showDropdown?: boolean
}

/**
 * Headless-ish autocomplete search input with a dropdown of suggestions.
 *
 * Suggestions come from the public `/search/suggest` endpoint (curated popular
 * topics + live content titles), debounced 150ms. The dropdown is keyboard
 * accessible (ArrowUp/Down to move, Enter to choose, Escape to close) and
 * closes on outside click / blur. Navigation behaviour is delegated to the
 * parent via `onSelectSuggestion` / `onSubmit` so the same component drives
 * both the header box and the full search-results page.
 */
export function SearchAutocomplete({
  value,
  onChange,
  onSelectSuggestion,
  onSubmit,
  placeholder,
  inputId,
  srLabel,
  inputClassName = 'input pl-11',
  leadingIcon,
  autoFocus,
  showDropdown = true,
}: Props) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<Suggestion[]>([])
  const [loading, setLoading] = useState(false)
  const [highlight, setHighlight] = useState(-1)
  const wrapRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Debounced fetch of suggestions whenever the value changes (or box opens empty).
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    const td = setTimeout(async () => {
      try {
        const res = await suggestSearch(value)
        if (!cancelled) {
          setItems(res)
          setHighlight(-1)
        }
      } catch {
        if (!cancelled) setItems([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, 150)
    return () => {
      cancelled = true
      clearTimeout(td)
    }
  }, [value, open])

  // Close on outside click.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus()
  }, [autoFocus])

  const choose = useCallback(
    (s: Suggestion | undefined) => {
      if (s) {
        setOpen(false)
        onSelectSuggestion(s)
      } else {
        const q = value.trim()
        setOpen(false)
        if (q) onSubmit(q)
      }
    },
    [value, onSelectSuggestion, onSubmit]
  )

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!open) {
        setOpen(true)
        return
      }
      setHighlight((i) => Math.min(i + 1, items.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight((i) => Math.max(i - 1, -1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      choose(highlight >= 0 ? items[highlight] : undefined)
    } else if (e.key === 'Escape') {
      setOpen(false)
      setHighlight(-1)
    }
  }

  // Group: curated topics first, then content hits.
  const curated = items.filter((s) => s.curated)
  const content = items.filter((s) => !s.curated)

  return (
    <div ref={wrapRef} className="relative">
      {leadingIcon}
      <label htmlFor={inputId} className="sr-only">
        {srLabel || t('header.searchSrLabel', 'Search TB Mukt Bharat')}
      </label>
      <input
        ref={inputRef}
        id={inputId}
        type="search"
        role="combobox"
        aria-expanded={open}
        aria-controls={`${inputId}-listbox`}
        aria-autocomplete="list"
        aria-activedescendant={highlight >= 0 ? `${inputId}-opt-${highlight}` : undefined}
        value={value}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          onChange(e.target.value)
          setOpen(true)
        }}
        onKeyDown={onKeyDown}
        onBlur={() => {
          // Delay so a click on a suggestion registers before we close.
          blurTimer.current = setTimeout(() => setOpen(false), 120)
        }}
        placeholder={placeholder}
        autoComplete="off"
        className={inputClassName}
      />

      {open && showDropdown && (items.length > 0 || loading) && (
        <div
          id={`${inputId}-listbox`}
          role="listbox"
          aria-label={t('pages.search.suggestions', 'Search suggestions')}
          className="absolute left-0 right-0 top-full mt-2 rounded-xl bg-white shadow-xl border border-snow-200 py-2 z-50 max-h-80 overflow-y-auto animate-scale-in origin-top"
        >
          {curated.length > 0 && (
            <>
              <p className="px-3 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wide text-snow-400">
                {t('pages.search.popularTopics', 'Popular topics')}
              </p>
              {curated.map((s, i) => (
                <SuggestionRow
                  key={`c-${i}`}
                  id={`${inputId}-opt-${i}`}
                  s={s}
                  active={highlight === i}
                  onEnter={() => choose(s)}
                  onHover={() => setHighlight(i)}
                />
              ))}
            </>
          )}
          {content.length > 0 && (
            <>
              {curated.length > 0 && <div className="my-1 border-t border-snow-100" />}
              <p className="px-3 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wide text-snow-400">
                {t('pages.search.contentMatches', 'Content')}
              </p>
              {content.map((s, i) => {
                const idx = curated.length + i
                return (
                  <SuggestionRow
                    key={`k-${i}`}
                    id={`${inputId}-opt-${idx}`}
                    s={s}
                    active={highlight === idx}
                    onEnter={() => choose(s)}
                    onHover={() => setHighlight(idx)}
                  />
                )
              })}
            </>
          )}
          {loading && items.length === 0 && (
            <p className="px-3 py-3 text-sm text-snow-400">{t('pages.search.loading', 'Loading…')}</p>
          )}
        </div>
      )}
    </div>
  )
}

function SuggestionRow({
  s,
  active,
  onEnter,
  onHover,
  id,
}: {
  s: Suggestion
  active: boolean
  onEnter: () => void
  onHover: () => void
  id: string
}) {
  const Icon = s.type ? TYPE_ICON[s.type] : Sparkles
  return (
    <button
      type="button"
      id={id}
      role="option"
      aria-selected={active}
      onMouseDown={(e) => {
        // Prevent the input blur firing before the click.
        e.preventDefault()
        onEnter()
      }}
      onMouseEnter={onHover}
      className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition ${
        active ? 'bg-[rgb(232_241_252)] text-[rgb(15_76_129)]' : 'text-snow-700 hover:bg-snow-50'
      }`}
    >
      <Icon className="w-4 h-4 shrink-0 opacity-70" aria-hidden="true" />
      <span className="truncate flex-1">{s.label}</span>
      {s.curated && (
        <span className="text-[10px] uppercase tracking-wide text-snow-400 shrink-0">{s.category}</span>
      )}
      {active && <CornerDownLeft className="w-3.5 h-3.5 opacity-60 shrink-0" aria-hidden="true" />}
    </button>
  )
}